import React, { useState, useEffect } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { dashboardApi } from '../../services/dashboardApi';
import { ArrowLeft } from 'lucide-react';

const CashbookReportDay = ({ date, onClose }) => {
    const { t } = useLang();
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);

    useEffect(() => {
        if (date) {
            _fetchDayReport();
        }
    }, [date]);

    const _fetchDayReport = async () => {
        try {
            setLoading(true);
            const response = await dashboardApi.getCashbookData();
            if (response?.success && response.data) {
                // Ensure entries is always an array
                let allEntries = [];
                const rawEntries = response.data.entries || [];
                
                if (rawEntries && typeof rawEntries === 'object' && !Array.isArray(rawEntries)) {
                    Object.values(rawEntries).forEach(dayList => {
                        if (Array.isArray(dayList)) allEntries = [...allEntries, ...dayList];
                    });
                } else {
                    allEntries = rawEntries;
                }

                // IMPROVED DATE MATCHING LOGIC
                const targetDateStr = new Date(date).toISOString().split('T')[0];

                const dayEntries = allEntries.filter(e => {
                    const entryDateRaw = e.date || e.entry_date || "";
                    if (!entryDateRaw) return false;
                    
                    // Convert entry date to YYYY-MM-DD for reliable comparison
                    const entryDateFormatted = new Date(entryDateRaw).toISOString().split('T')[0];
                    return entryDateFormatted === targetDateStr;
                });

                let totalIn = 0;
                let totalOut = 0;
                dayEntries.forEach(entry => {
                    const amount = parseFloat(entry.amount) || 0;
                    const type = (entry.type || entry.entry_type || "").toLowerCase();
                    if (type === 'in') totalIn += amount;
                    else totalOut += amount;
                });

                setReportData({
                    summary: { total_in: totalIn, total_out: totalOut, daily_balance: totalIn - totalOut },
                    entries: dayEntries,
                });
            }
        } catch (error) {
            console.error("Critical Day Report Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const _formatCurrency = (amount) => {
        return '₹ ' + new Intl.NumberFormat('en-IN').format(Math.abs(parseFloat(amount) || 0));
    };

    if (loading) return (
        <div style={styles.center}>
            <div className="loader"></div>
        </div>
    );
    
    const summary = reportData?.summary;
    const entries = reportData?.entries || [];

    return (
        <div style={styles.scaffold}>
            <div style={styles.appBar}>
                <button style={styles.backBtn} onClick={onClose}><ArrowLeft size={22} color="#FFF" /></button>
                <div style={styles.appBarTitleArea}>
                    <div style={styles.appBarTitle}>
                        {new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                    <div style={styles.appBarSubTitle}>{t(LangKeys.daily_details)}</div>
                </div>
            </div>

            <div style={styles.body}>
                <div style={styles.summaryCard}>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>{t(LangKeys.in_entry)}</span>
                        <span style={{...styles.summaryValue, color: '#16A34A'}}>{_formatCurrency(summary?.total_in)}</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>{t(LangKeys.out_entry)}</span>
                        <span style={{...styles.summaryValue, color: '#DC2626'}}>{_formatCurrency(summary?.total_out)}</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>{t(LangKeys.daily_balance_short)}</span>
                        <span style={{...styles.summaryValue, color: (summary?.daily_balance || 0) >= 0 ? '#16A34A' : '#DC2626'}}>
                            {_formatCurrency(summary?.daily_balance)}
                        </span>
                    </div>
                </div>

                <div style={styles.listContainer}>
                    {entries.length === 0 ? (
                        <div style={styles.emptyState}>
                            <img src="https://khata.moxpal.com/assets/empty-cashbook.png" alt="empty" style={{width: '100px', opacity: 0.5, marginBottom: '10px'}} />
                            <div>{t(LangKeys.no_entries_on_this_day)}</div>
                        </div>
                    ) : (
                        entries.map((entry, index) => {
                            const isIN = (entry.type || entry.entry_type || "").toLowerCase() === 'in';
                            const color = isIN ? '#16A34A' : '#DC2626';
                            const rawDate = entry.date || entry.entry_date || "";
                            const time = rawDate.includes(' ') ? rawDate.split(' ')[1].substring(0, 5) : "";

                            return (
                                <div key={entry.id || index} style={styles.entryCard}>
                                    <div style={styles.entryRowTop}>
                                        <div style={{...styles.entryAmountText, color}}>{_formatCurrency(entry.amount)}</div>
                                        <div style={{...styles.entryTypeText, color}}>{isIN ? t(LangKeys.in_entry).toUpperCase() : t(LangKeys.out_entry).toUpperCase()}</div>
                                    </div>
                                    <div style={styles.entryFooter}>
                                        {(entry.payment_mode || entry.mode || 'cash').toUpperCase()} • {entry.note || '---'} {time && `• ${time}`}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    scaffold: { display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#F3F4F6', borderRadius: '16px', overflow: 'hidden', padding: '16px' },
    appBar: { backgroundColor: '#1D4ED8', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0, borderRadius: '14px 14px 0 0' },
    backBtn: { border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' },
    appBarTitleArea: { display: 'flex', flexDirection: 'column' },
    appBarTitle: { color: '#FFF', fontSize: '18px', fontWeight: '700' },
    appBarSubTitle: { color: 'rgba(255,255,255,0.7)', fontSize: '12px' },
    body: { flex: 1, overflowY: 'auto', padding: '14px' },
    summaryCard: { padding: '16px', backgroundColor: '#FFF', borderRadius: '14px', display: 'flex', justifyContent: 'space-around', marginBottom: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
    summaryItem: { textAlign: 'center' },
    summaryLabel: { color: '#6B7280', fontSize: '12px', display: 'block', marginBottom: '2px', fontWeight: '600' },
    summaryValue: { fontSize: '16px', fontWeight: '800' },
    listContainer: { display: 'flex', flexDirection: 'column', gap: '10px' },
    entryCard: { backgroundColor: '#FFF', borderRadius: '14px', padding: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', border: '1px solid #F1F5F9' },
    entryRowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
    entryAmountText: { fontSize: '19px', fontWeight: '800' },
    entryTypeText: { fontWeight: '800', fontSize: '11px' },
    entryFooter: { fontSize: '12px', color: '#9CA3AF' },
    emptyState: { textAlign: 'center', padding: '60px 20px', color: '#9CA3AF', display: 'flex', flexDirection: 'column', alignItems: 'center' },
    center: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', backgroundColor: '#F3F4F6' }
};

export default CashbookReportDay;
